import { CustomerOrderLineFormApi } from "src/api/generated/erp/sales/customerOrder/api/customerOrderLineFormApi.ts";
import { StockPartConfigurationService_AvailableStockConfiguration } from "src/api/generated/erp/sales/customerOrder/service/stockPartConfigurationService.ts";
import { AsyncButton } from "src/components/common/buttons/AsyncButton.tsx";
import { AavoDataGrid } from "src/components/common/dataGrid/AavoDataGrid.tsx";
import { floatColumn, integerColumn, textColumn } from "src/components/common/dataGrid/columns.tsx";
import { ClientSideDataGridModel } from "src/components/common/dataGrid/gridModel/ClientSideDataGridModel.tsx";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";
import { FormResult } from "src/components/common/forms/types.ts";
import { faCheck } from "@fortawesome/pro-regular-svg-icons";
import i18n from "i18next";

export interface StockPartConfigurationSelectionDataGridProps {
	customerOrderId: number;
	salesPartId: number;
	salesQuantityAsSalesUnits: number;
	onCompleted: (result: FormResult<number>) => Promise<void>;
}

export function StockPartConfigurationSelectionDataGrid({
	customerOrderId,
	salesPartId,
	salesQuantityAsSalesUnits,
	onCompleted,
}: StockPartConfigurationSelectionDataGridProps) {
	return (
		<ClientSideDataGridModel
			fetchData={() =>
				CustomerOrderLineFormApi.getAvailableStockConfigurations({
					customerOrderId,
					salesPartId,
					salesQuantityAsSalesUnits,
				})
			}
			getRows={(data) => data}
			initialParams={{}}
			getRowId={(row) => row.partConfigurationId}
			gridId="D60FB19D1D4D4CB3"
			render={({ dataGridProps, onlySelectedRow }) => (
				<AavoDataGrid<StockPartConfigurationService_AvailableStockConfiguration>
					disableMultipleRowSelection
					columns={[
						integerColumn({
							field: "partConfigurationId",
							headerName: i18n.t("configuration"),
							width: 130,
						}),
						textColumn({
							field: "description",
							headerName: i18n.t("configuration_description"),
							width: 300,
						}),
						textColumn({
							field: "warehouseLocations",
							headerName: i18n.t("warehouse_locations"),
							width: 220,
						}),
						floatColumn({
							field: "stockQuantity",
							headerName: i18n.t("stock_quantity"),
							width: 120,
						}),
						floatColumn({
							field: "reservedQuantity",
							headerName: i18n.t("reserved"),
							width: 120,
						}),
						floatColumn({
							field: "availableQuantity",
							headerName: i18n.t("available"),
							width: 120,
						}),
					]}
					onRowDoubleClick={async ({ row }) =>
						onCompleted({ type: "success", value: row.partConfigurationId })
					}
					actionBarComponents={
						<AsyncButton
							label={i18n.t("select_action")}
							icon={faCheck}
							disabled={!onlySelectedRow}
							onClick={async () => {
								if (!onlySelectedRow) return;
								await onCompleted({ type: "success", value: onlySelectedRow.partConfigurationId });
							}}
						/>
					}
					agentContext={{
						key: defineAgentUiContextKey("erp.sales.stockPartConfigurationSelection.grid"),
						data: { customerOrderId, salesPartId, salesQuantityAsSalesUnits },
						rowFields: ["partConfigurationId"],
						selectedRowPathSegment: (row) => `${i18n.t("configuration")} ${row.partConfigurationId}`,
					}}
					{...dataGridProps}
				/>
			)}
		/>
	);
}
